import "../../components/EventUserDashboard.css";
import AdminBackBar from "../../components/admin/AdminBackBar";
import Billede1 from "../../Images/Billede1.jpg";
import HeroSectionV2 from "../../components/HeroSectionV2";

import React, { useEffect, useMemo, useState } from "react";

import {
    Alert,
    Badge,
    Button,
    Container,
    Divider,
    Grid,
    Group,
    Loader,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
    Title,
} from "@mantine/core";

import {
    IconCalendar,
    IconGlass,
    IconPrinter,
    IconQrcode,
    IconTicket,
} from "@tabler/icons-react";

import QRCode from "react-qr-code";

// ---------------------------------------------------------
// TYPES
// ---------------------------------------------------------

interface EventDto {
    eventID: number;
    eventName?: string;
}

interface DrinkTicketCollectionDto {
    drinkTicketCollectionId: number;
    collectionName: string;
    eventBarId: number;
    maxDrinksPerTicket: number;
    ticketCount: number;
    createdAt?: string;
    validDate?: string | null;
}

interface DrinkTicketDto {
    drinkTicketId: number;
    ticketCode: string;
    collectionName: string;
    ticketNumber: number;
    eventBarId: number;
    maxDrinks: number;
    isActive: boolean;
}

type EventBar = {
    eventBarId: number;
    name: string;
    isActive: boolean;
};

// ---------------------------------------------------------
// HELPERS
// ---------------------------------------------------------

const toArray = <T,>(result: any): T[] => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.items)) return result.items;
    if (Array.isArray(result?.results)) return result.results;

    return [];
};

const formatDate = (value?: string | null) => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "";
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
};

// ---------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------

const AdminDrinkTicketPrint: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL;

    // -----------------------------------------------------
    // STATE
    // -----------------------------------------------------

    const [events, setEvents] = useState<EventDto[]>([]);
    const [eventId, setEventId] = useState<number>(0);

    const [bars, setBars] = useState<EventBar[]>([]);

    const [collections, setCollections] =
        useState<DrinkTicketCollectionDto[]>([]);

    const [selectedCollectionId, setSelectedCollectionId] =
        useState<number>(0);

    const [tickets, setTickets] = useState<DrinkTicketDto[]>([]);

    const [error, setError] = useState<string | null>(null);

    const [loadingCollections, setLoadingCollections] =
        useState(false);

    const [loadingTickets, setLoadingTickets] =
        useState(false);

    // -----------------------------------------------------
    // EVENTS
    // -----------------------------------------------------

    useEffect(() => {
        const loadEvents = async () => {
            try {
                const res = await fetch(
                    `${apiUrl}/api/events`
                );

                if (!res.ok) return;

                const result = await res.json();

                setEvents(
                    toArray<EventDto>(result)
                );
            } catch (err) {
                console.error(
                    "Error loading events:",
                    err
                );
            }
        };

        loadEvents();
    }, [apiUrl]);

    // -----------------------------------------------------
    // BARS
    // -----------------------------------------------------

    const loadBars = async (id: number) => {
        try {
            const res = await fetch(
                `${apiUrl}/api/EventBars/event/${id}`
            );

            if (!res.ok) {
                setBars([]);
                return;
            }

            const result = await res.json();

            setBars(
                toArray<EventBar>(result)
            );
        } catch (err) {
            console.error(err);
            setBars([]);
        }
    };

    const getBarName = (barId: number) => {
        return (
            bars.find(
                (bar) =>
                    bar.eventBarId === barId
            )?.name ?? `Bar ${barId}`
        );
    };

    // -----------------------------------------------------
    // COLLECTIONS
    // -----------------------------------------------------

    const loadCollections = async (
        currentEventId: number
    ) => {
        if (!currentEventId) {
            setCollections([]);
            return;
        }

        try {
            setLoadingCollections(true);
            setError(null);

            const res = await fetch(
                `${apiUrl}/api/admin/drink-ticket-collections?eventId=${currentEventId}`
            );

            if (!res.ok) {
                const text = await res.text();

                throw new Error(
                    text ||
                    "Could not load collections."
                );
            }

            const result = await res.json();

            setCollections(
                toArray<DrinkTicketCollectionDto>(
                    result
                )
            );
        } catch (err: any) {
            console.error(
                "Error loading collections:",
                err
            );

            setCollections([]);

            setError(
                err?.message ||
                "Could not load collections."
            );
        } finally {
            setLoadingCollections(false);
        }
    };

    // -----------------------------------------------------
    // TICKETS
    // -----------------------------------------------------

    const loadTickets = async (
        collectionId: number
    ) => {
        if (!collectionId) {
            setTickets([]);
            return;
        }

        try {
            setLoadingTickets(true);
            setError(null);

            const res = await fetch(
                `${apiUrl}/api/admin/drink-ticket-collections/${collectionId}/tickets`
            );

            if (!res.ok) {
                const text = await res.text();

                throw new Error(
                    text ||
                    "Could not load tickets."
                );
            }

            const result = await res.json();

            setTickets(
                toArray<DrinkTicketDto>(
                    result
                )
            );
        } catch (err: any) {
            console.error(
                "Error loading tickets:",
                err
            );

            setTickets([]);

            setError(
                err?.message ||
                "Could not load tickets."
            );
        } finally {
            setLoadingTickets(false);
        }
    };

    // -----------------------------------------------------
    // EVENT CHANGE
    // -----------------------------------------------------

    useEffect(() => {
        setSelectedCollectionId(0);
        setTickets([]);
        setError(null);

        if (eventId > 0) {
            loadBars(eventId);
            loadCollections(eventId);
        } else {
            setBars([]);
            setCollections([]);
        }
    }, [eventId]);

    // -----------------------------------------------------
    // COLLECTION CHANGE
    // -----------------------------------------------------

    useEffect(() => {
        if (selectedCollectionId > 0) {
            loadTickets(
                selectedCollectionId
            );
        } else {
            setTickets([]);
        }
    }, [selectedCollectionId]);

    // -----------------------------------------------------
    // SELECTED COLLECTION
    // -----------------------------------------------------

    const selectedCollection =
        useMemo(() => {
            return (
                collections.find(
                    (collection) =>
                        collection.drinkTicketCollectionId ===
                        selectedCollectionId
                ) ?? null
            );
        }, [
            collections,
            selectedCollectionId,
        ]);

    // -----------------------------------------------------
    // ACTIVE TICKETS
    // -----------------------------------------------------

    const activeTicketCount =
        useMemo(
            () =>
                tickets.filter(
                    (ticket) =>
                        ticket.isActive
                ).length,
            [tickets]
        );

    // -----------------------------------------------------
    // PRINT
    // -----------------------------------------------------

    const printPage = () => {
        window.print();
    };

    // -----------------------------------------------------
    // QR URL
    // -----------------------------------------------------

    const getQrValue = (
        ticket: DrinkTicketDto
    ) => {
        return `${window.location.origin}/form-drink-order/${eventId}/${encodeURIComponent(
            ticket.ticketCode
        )}?eventBarId=${selectedCollection?.eventBarId
            }`;
    };

    // -----------------------------------------------------
    // DISPLAY TICKET CODE
    // -----------------------------------------------------

    const getDisplayTicketCode = (
        ticketCode: string
    ) => {
        return ticketCode?.replace(
            /-\d+-/,
            "-"
        );
    };

    // -----------------------------------------------------
    // RENDER
    // -----------------------------------------------------

    return (
        <div>
            {/* ================================================= */}
            {/* HERO */}
            {/* ================================================= */}

            <div className="no-print">
                <HeroSectionV2
                    titleEn="Print Ticket Codes"
                    titleDa="Print Ticket Koderne"
                    descriptionEn="Select event and collection to print QR codes"
                    descriptionDa="Vælg et event og collection for at printe QR koderne"
                    imageSrc={Billede1}
                />

                <AdminBackBar
                    title="Drinks"
                    crumbs={[
                        {
                            label: "Admin",
                            to: "/admin",
                        },
                        {
                            label: "Drinks",
                        },
                    ]}
                />
            </div>

            {/* ================================================= */}
            {/* ADMIN CONTENT */}
            {/* ================================================= */}

            <Container
                size="xl"
                py="xl"
                className="no-print"
            >
                <Stack gap="xl">

                    {/* ERROR */}

                    {error && (
                        <Alert
                            color="red"
                            title="Error"
                            withCloseButton
                            onClose={() =>
                                setError(null)
                            }
                        >
                            {error}
                        </Alert>
                    )}

                    {/* ========================================= */}
                    {/* SELECTION */}
                    {/* ========================================= */}

                    <Paper
                        withBorder
                        radius="lg"
                        shadow="xs"
                        style={{
                            overflow: "hidden",
                        }}
                    >
                        <Group
                            p="lg"
                            justify="space-between"
                        >
                            <Group gap="sm">
                                <ThemeIcon
                                    color="red"
                                    variant="light"
                                    size="lg"
                                    radius="md"
                                >
                                    <IconPrinter
                                        size={19}
                                    />
                                </ThemeIcon>

                                <div>
                                    <Title order={4}>
                                        Print ticket codes
                                    </Title>

                                    <Text
                                        size="sm"
                                        c="dimmed"
                                    >
                                        Select an event and ticket collection
                                    </Text>
                                </div>
                            </Group>
                        </Group>

                        <Divider />

                        <Stack
                            p="lg"
                            gap="lg"
                        >
                            <Grid
                                align="flex-end"
                            >
                                {/* EVENT */}

                                <Grid.Col
                                    span={{
                                        base: 12,
                                        md: 6,
                                    }}
                                >
                                    <Select
                                        label="Event"
                                        placeholder="Select event..."
                                        value={
                                            eventId
                                                ? String(
                                                    eventId
                                                )
                                                : null
                                        }
                                        onChange={(
                                            value
                                        ) =>
                                            setEventId(
                                                value
                                                    ? Number(
                                                        value
                                                    )
                                                    : 0
                                            )
                                        }
                                        data={events.map(
                                            (
                                                event
                                            ) => ({
                                                value:
                                                    String(
                                                        event.eventID
                                                    ),

                                                label:
                                                    event.eventName ||
                                                    `Event ${event.eventID}`,
                                            })
                                        )}
                                        searchable
                                        clearable
                                    />
                                </Grid.Col>

                                {/* COLLECTION */}

                                <Grid.Col
                                    span={{
                                        base: 12,
                                        md: 6,
                                    }}
                                >
                                    <Select
                                        label="Collection"
                                        searchable
                                        clearable
                                        disabled={
                                            !eventId ||
                                            loadingCollections
                                        }
                                        placeholder={
                                            !eventId
                                                ? "Select event first..."
                                                : loadingCollections
                                                    ? "Loading collections..."
                                                    : "Select collection..."
                                        }
                                        value={
                                            selectedCollectionId
                                                ? String(
                                                    selectedCollectionId
                                                )
                                                : null
                                        }
                                        onChange={(
                                            value
                                        ) =>
                                            setSelectedCollectionId(
                                                value
                                                    ? Number(
                                                        value
                                                    )
                                                    : 0
                                            )
                                        }
                                        data={collections.map(
                                            (
                                                collection
                                            ) => ({
                                                value:
                                                    String(
                                                        collection.drinkTicketCollectionId
                                                    ),

                                                label:
                                                    `${collection.collectionName} · ${getBarName(
                                                        collection.eventBarId
                                                    )} · ${collection.ticketCount} tickets` +
                                                    (collection.validDate
                                                        ? ` · ${formatDate(
                                                            collection.validDate
                                                        )}`
                                                        : ""),
                                            })
                                        )}
                                        rightSection={
                                            loadingCollections
                                                ? (
                                                    <Loader
                                                        size={
                                                            15
                                                        }
                                                        color="red"
                                                    />
                                                )
                                                : undefined
                                        }
                                    />
                                </Grid.Col>
                            </Grid>

                            {/* ================================= */}
                            {/* SELECTED COLLECTION SUMMARY */}
                            {/* ================================= */}

                            {selectedCollection && (
                                <>
                                    <Divider />

                                    <div>
                                        <Text
                                            fw={700}
                                            size="sm"
                                            mb="xs"
                                        >
                                            Selected collection
                                        </Text>

                                        <Text
                                            size="xs"
                                            c="dimmed"
                                            mb="md"
                                        >
                                            Check the collection before printing
                                        </Text>

                                        <SimpleGrid
                                            cols={{
                                                base: 2,
                                                sm: 3,
                                                md: 5,
                                            }}
                                            spacing="md"
                                        >
                                            {/* COLLECTION */}

                                            <Paper
                                                withBorder
                                                radius="md"
                                                p="md"
                                            >
                                                <Text
                                                    size="xs"
                                                    c="dimmed"
                                                >
                                                    Collection
                                                </Text>

                                                <Text
                                                    fw={700}
                                                    mt={3}
                                                >
                                                    {
                                                        selectedCollection.collectionName
                                                    }
                                                </Text>
                                            </Paper>

                                            {/* BAR */}

                                            <Paper
                                                withBorder
                                                radius="md"
                                                p="md"
                                            >
                                                <Text
                                                    size="xs"
                                                    c="dimmed"
                                                >
                                                    Bar
                                                </Text>

                                                <Text
                                                    fw={700}
                                                    mt={3}
                                                >
                                                    {getBarName(
                                                        selectedCollection.eventBarId
                                                    )}
                                                </Text>
                                            </Paper>

                                            {/* TICKETS */}

                                            <Paper
                                                withBorder
                                                radius="md"
                                                p="md"
                                            >
                                                <Text
                                                    size="xs"
                                                    c="dimmed"
                                                >
                                                    Tickets
                                                </Text>

                                                <Text
                                                    fw={700}
                                                    mt={3}
                                                >
                                                    {
                                                        selectedCollection.ticketCount
                                                    }
                                                </Text>
                                            </Paper>

                                            {/* DRINKS */}

                                            <Paper
                                                withBorder
                                                radius="md"
                                                p="md"
                                            >
                                                <Text
                                                    size="xs"
                                                    c="dimmed"
                                                >
                                                    Drinks / ticket
                                                </Text>

                                                <Text
                                                    fw={700}
                                                    mt={3}
                                                >
                                                    {
                                                        selectedCollection.maxDrinksPerTicket
                                                    }
                                                </Text>
                                            </Paper>

                                            {/* DATE */}

                                            <Paper
                                                withBorder
                                                radius="md"
                                                p="md"
                                            >
                                                <Text
                                                    size="xs"
                                                    c="dimmed"
                                                >
                                                    Valid date
                                                </Text>

                                                <Text
                                                    fw={700}
                                                    mt={3}
                                                >
                                                    {selectedCollection.validDate
                                                        ? formatDate(
                                                            selectedCollection.validDate
                                                        )
                                                        : "All days"}
                                                </Text>
                                            </Paper>
                                        </SimpleGrid>
                                    </div>

                                    {/* PRINT ACTION */}

                                    <Group
                                        justify="space-between"
                                        align="center"
                                    >
                                        <Group gap="xs">
                                            <Badge
                                                color="green"
                                                variant="light"
                                                size="lg"
                                            >
                                                {
                                                    activeTicketCount
                                                }{" "}
                                                active
                                            </Badge>

                                            {tickets.length -
                                                activeTicketCount >
                                                0 && (
                                                    <Badge
                                                        color="gray"
                                                        variant="light"
                                                        size="lg"
                                                    >
                                                        {tickets.length -
                                                            activeTicketCount}{" "}
                                                        used
                                                    </Badge>
                                                )}
                                        </Group>

                                        <Button
                                            color="red"
                                            size="md"
                                            leftSection={
                                                <IconPrinter
                                                    size={
                                                        18
                                                    }
                                                />
                                            }
                                            onClick={
                                                printPage
                                            }
                                            loading={
                                                loadingTickets
                                            }
                                            disabled={
                                                tickets.length ===
                                                0
                                            }
                                        >
                                            Print{" "}
                                            {
                                                tickets.length
                                            }{" "}
                                            tickets
                                        </Button>
                                    </Group>
                                </>
                            )}
                        </Stack>
                    </Paper>

                    {/* ========================================= */}
                    {/* PREVIEW */}
                    {/* ========================================= */}

                    {selectedCollection && (
                        <Paper
                            withBorder
                            radius="lg"
                            shadow="xs"
                            style={{
                                overflow:
                                    "hidden",
                            }}
                        >
                            {/* PREVIEW HEADER */}

                            <Group
                                justify="space-between"
                                p="lg"
                            >
                                <Group gap="sm">
                                    <ThemeIcon
                                        color="red"
                                        variant="light"
                                        size="lg"
                                        radius="md"
                                    >
                                        <IconQrcode
                                            size={19}
                                        />
                                    </ThemeIcon>

                                    <div>
                                        <Group gap="xs">
                                            <Title
                                                order={
                                                    4
                                                }
                                            >
                                                Print preview
                                            </Title>

                                            {!loadingTickets &&
                                                tickets.length >
                                                0 && (
                                                    <Badge
                                                        color="red"
                                                        variant="light"
                                                    >
                                                        {
                                                            tickets.length
                                                        }
                                                    </Badge>
                                                )}
                                        </Group>

                                        <Text
                                            size="sm"
                                            c="dimmed"
                                        >
                                            {
                                                selectedCollection.collectionName
                                            }
                                        </Text>
                                    </div>
                                </Group>

                                {!loadingTickets &&
                                    tickets.length >
                                    0 && (
                                        <Button
                                            color="red"
                                            variant="light"
                                            leftSection={
                                                <IconPrinter
                                                    size={
                                                        17
                                                    }
                                                />
                                            }
                                            onClick={
                                                printPage
                                            }
                                        >
                                            Print
                                        </Button>
                                    )}
                            </Group>

                            <Divider />

                            {/* LOADING */}

                            {loadingTickets && (
                                <Stack
                                    align="center"
                                    justify="center"
                                    p={70}
                                >
                                    <Loader
                                        color="red"
                                        size="sm"
                                    />

                                    <Text
                                        size="sm"
                                        c="dimmed"
                                    >
                                        Loading tickets...
                                    </Text>
                                </Stack>
                            )}

                            {/* EMPTY */}

                            {!loadingTickets &&
                                tickets.length ===
                                0 && (
                                    <Stack
                                        align="center"
                                        justify="center"
                                        p={70}
                                        gap="xs"
                                    >
                                        <ThemeIcon
                                            color="gray"
                                            variant="light"
                                            size={54}
                                            radius="xl"
                                        >
                                            <IconTicket
                                                size={
                                                    24
                                                }
                                            />
                                        </ThemeIcon>

                                        <Text
                                            fw={600}
                                        >
                                            No tickets found
                                        </Text>

                                        <Text
                                            size="sm"
                                            c="dimmed"
                                        >
                                            This collection does not contain any tickets.
                                        </Text>
                                    </Stack>
                                )}

                            {/* SCREEN PREVIEW */}

                            {!loadingTickets &&
                                tickets.length >
                                0 && (
                                    <div
                                        style={{
                                            padding:
                                                24,

                                            background:
                                                "var(--mantine-color-gray-0)",
                                        }}
                                    >
                                        <SimpleGrid
                                            cols={{
                                                base: 1,
                                                sm: 2,
                                                md: 3,
                                                lg: 4,
                                            }}
                                            spacing="md"
                                        >
                                            {tickets.map(
                                                (
                                                    ticket
                                                ) => (
                                                    <Paper
                                                        key={
                                                            ticket.drinkTicketId
                                                        }
                                                        withBorder
                                                        radius="lg"
                                                        p="lg"
                                                        bg="white"
                                                        style={{
                                                            position:
                                                                "relative",
                                                        }}
                                                    >
                                                        <Stack
                                                            align="center"
                                                            gap="sm"
                                                        >
                                                            {/* COLLECTION */}

                                                            <div
                                                                style={{
                                                                    width:
                                                                        "100%",
                                                                    textAlign:
                                                                        "center",
                                                                }}
                                                            >
                                                                <Text
                                                                    fw={
                                                                        800
                                                                    }
                                                                    size="sm"
                                                                >
                                                                    {
                                                                        selectedCollection.collectionName
                                                                    }
                                                                </Text>

                                                                <Text
                                                                    size="xs"
                                                                    c="dimmed"
                                                                >
                                                                    Drink ticket
                                                                </Text>
                                                            </div>

                                                            {/* QR */}

                                                            <Paper
                                                                p="sm"
                                                                radius="md"
                                                                withBorder
                                                                bg="white"
                                                            >
                                                                <QRCode
                                                                    value={getQrValue(
                                                                        ticket
                                                                    )}
                                                                    size={
                                                                        155
                                                                    }
                                                                    bgColor="#FFFFFF"
                                                                    fgColor="#000000"
                                                                    level="M"
                                                                />
                                                            </Paper>

                                                            {/* CODE */}

                                                            <Text
                                                                fw={
                                                                    800
                                                                }
                                                                size="lg"
                                                                style={{
                                                                    letterSpacing:
                                                                        "0.04em",

                                                                    fontFamily:
                                                                        "monospace",
                                                                }}
                                                            >
                                                                {getDisplayTicketCode(
                                                                    ticket.ticketCode
                                                                )}
                                                            </Text>

                                                            {/* INFO */}

                                                            <Group
                                                                gap="xs"
                                                                justify="center"
                                                            >
                                                                <Badge
                                                                    color="red"
                                                                    variant="light"
                                                                >
                                                                    {getBarName(
                                                                        selectedCollection.eventBarId
                                                                    )}
                                                                </Badge>

                                                                <Badge
                                                                    color="gray"
                                                                    variant="light"
                                                                    leftSection={
                                                                        <IconGlass
                                                                            size={
                                                                                12
                                                                            }
                                                                        />
                                                                    }
                                                                >
                                                                    {
                                                                        ticket.maxDrinks
                                                                    }{" "}
                                                                    drinks
                                                                </Badge>
                                                            </Group>

                                                            {/* DATE */}

                                                            {selectedCollection.validDate && (
                                                                <Group
                                                                    gap={
                                                                        5
                                                                    }
                                                                >
                                                                    <IconCalendar
                                                                        size={
                                                                            14
                                                                        }
                                                                        color="var(--mantine-color-gray-6)"
                                                                    />

                                                                    <Text
                                                                        size="xs"
                                                                        c="dimmed"
                                                                    >
                                                                        Only valid{" "}
                                                                        {formatDate(
                                                                            selectedCollection.validDate
                                                                        )}
                                                                    </Text>
                                                                </Group>
                                                            )}

                                                            {/* STATUS */}

                                                            {!ticket.isActive && (
                                                                <Badge
                                                                    color="gray"
                                                                    variant="filled"
                                                                    pos="absolute"
                                                                    top={
                                                                        10
                                                                    }
                                                                    right={
                                                                        10
                                                                    }
                                                                >
                                                                    Used
                                                                </Badge>
                                                            )}
                                                        </Stack>
                                                    </Paper>
                                                )
                                            )}
                                        </SimpleGrid>
                                    </div>
                                )}
                        </Paper>
                    )}
                </Stack>
            </Container>

            {/* ================================================= */}
            {/* ACTUAL PRINT AREA */}
            {/* ================================================= */}

            {selectedCollection &&
                !loadingTickets &&
                tickets.length > 0 && (
                    <div className="print-only-area">
                        <div className="tickets-print-grid">
                            {tickets.map(
                                (ticket) => (
                                    <div
                                        key={
                                            ticket.drinkTicketId
                                        }
                                        className="ticket-print-card"
                                    >
                                        <div className="ticket-print-content">

                                            {/* COLLECTION NAME */}

                                            <div className="ticket-print-name">
                                                {
                                                    selectedCollection.collectionName
                                                }
                                            </div>

                                            {/* QR CODE */}

                                            <div className="ticket-print-qr">
                                                <QRCode
                                                    value={getQrValue(
                                                        ticket
                                                    )}
                                                    size={
                                                        190
                                                    }
                                                    bgColor="#FFFFFF"
                                                    fgColor="#000000"
                                                    level="M"
                                                />
                                            </div>

                                            {/* CODE */}

                                            <div className="ticket-print-code">
                                                {getDisplayTicketCode(
                                                    ticket.ticketCode
                                                )}
                                            </div>

                                            {/* BAR / DRINKS */}

                                            <div className="ticket-print-meta">
                                                <span>
                                                    {getBarName(
                                                        selectedCollection.eventBarId
                                                    )}
                                                </span>

                                                <span>
                                                    {
                                                        ticket.maxDrinks
                                                    }{" "}
                                                    drinks
                                                </span>
                                            </div>

                                            {/* DATE */}

                                            {selectedCollection.validDate && (
                                                <div className="ticket-print-date">
                                                    Only valid{" "}
                                                    {formatDate(
                                                        selectedCollection.validDate
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                )}

            {/* ================================================= */}
            {/* PRINT CSS */}
            {/* ================================================= */}

            <style>{`

                /* --------------------------------------------- */
                /* HIDE REAL PRINT AREA ON SCREEN                */
                /* --------------------------------------------- */

                .print-only-area {
                    display: none;
                }


                /* --------------------------------------------- */
                /* PRINT                                        */
                /* --------------------------------------------- */

                @media print {

                    @page {
                        size: A4;
                        margin: 4mm;
                    }


                    html,
                    body {
                        margin: 0 !important;
                        padding: 0 !important;
                        background: white !important;
                    }


                    body * {
                        visibility: hidden;
                    }


                    .print-only-area,
                    .print-only-area * {
                        visibility: visible;
                    }


                    .print-only-area {
                        display: block !important;

                        position: absolute;

                        top: 0;
                        left: 0;

                        width: 100%;

                        margin: 0;
                        padding: 0;
                    }


                    .no-print {
                        display: none !important;
                    }


                    /* ----------------------------------------- */
                    /* GRID                                      */
                    /* ----------------------------------------- */

                    .tickets-print-grid {
                        display: grid !important;

                        grid-template-columns:
                            repeat(4, 1fr) !important;

                        gap: 0 !important;

                        width: 100%;
                    }


                    /* ----------------------------------------- */
                    /* TICKET                                    */
                    /* ----------------------------------------- */

                    .ticket-print-card {
                        height: 71mm !important;
                        min-height: 71mm !important;

                        box-sizing: border-box;

                        border: 1px solid #000 !important;

                        border-radius: 0 !important;

                        box-shadow: none !important;

                        padding: 2mm !important;

                        background: #fff !important;

                        page-break-inside: avoid;
                        break-inside: avoid;

                        overflow: hidden;
                    }


                    .ticket-print-content {
                        height: 100%;

                        display: flex;

                        flex-direction: column;

                        align-items: center;

                        justify-content: flex-start;

                        text-align: center;

                        color: #000;
                    }


                    /* ----------------------------------------- */
                    /* COLLECTION                                */
                    /* ----------------------------------------- */

                    .ticket-print-name {
                        width: 100%;

                        font-size: 9pt;

                        line-height: 1.1;

                        font-weight: 700;

                        margin-bottom: 1.5mm;

                        white-space: nowrap;

                        overflow: hidden;

                        text-overflow: ellipsis;
                    }


                    /* ----------------------------------------- */
                    /* QR                                        */
                    /* ----------------------------------------- */

                    .ticket-print-qr {
                        display: flex;

                        justify-content: center;

                        align-items: center;

                        margin-bottom: 1.5mm;
                    }


                    .ticket-print-qr svg {
                        width: 120px !important;
                        height: 120px !important;
                    }


                    /* ----------------------------------------- */
                    /* CODE                                      */
                    /* ----------------------------------------- */

                    .ticket-print-code {
                        font-size: 10pt;

                        line-height: 1.1;

                        font-weight: 800;

                        font-family: monospace;

                        letter-spacing: 0.02em;

                        margin-bottom: 1.5mm;
                    }


                    /* ----------------------------------------- */
                    /* META                                      */
                    /* ----------------------------------------- */

                    .ticket-print-meta {
                        width: 100%;

                        display: flex;

                        justify-content: center;

                        align-items: center;

                        gap: 2mm;

                        font-size: 7.5pt;

                        font-weight: 600;

                        line-height: 1.1;
                    }


                    .ticket-print-meta span + span::before {
                        content: "•";

                        margin-right: 2mm;
                    }


                    /* ----------------------------------------- */
                    /* DATE                                      */
                    /* ----------------------------------------- */

                    .ticket-print-date {
                        font-size: 7pt;

                        line-height: 1.1;

                        margin-top: 1.5mm;
                    }
                }

            `}</style>
        </div>
    );
};

export default AdminDrinkTicketPrint;